import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Plus, Edit, Trash2, Eye, User, Users, Mail, Phone,
  MapPin, Building, Building2, CreditCard, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown,
  X, AlertCircle, CheckCircle, XCircle, Filter, Download, Upload, Loader2,
  Calendar, FileText, DollarSign, TrendingUp, TrendingDown, Clock,
  Settings, Package, ShoppingBag, Smartphone, Copy, Star, Sparkles,
  Percent, Tag, Boxes, BarChart3,
  ShoppingCart, LogOut, Menu, FileSpreadsheet,
  Trash, Truck
} from 'lucide-react';
import api from '../../../services/api';
import {
  solicitudesService,
  solicitudesArchivosService,
  articulosService,
  serviciosService,
  subserviciosService,
  categoriasSolicitudService,
  empresasService,
  clientesService,
  usuariosService,
  solicitudesArticulosService,
  cotizacionesService,
  UPLOADS_BASE_URL
} from '../../../services/api';
import { useApp } from '../../../context/AppContext';
import $ from 'jquery';
import 'select2';
import 'select2/dist/css/select2.min.css';
import ArticuloItem from '../../common/ArticuloItem';
import useModal from '../../../hooks/useModal';

// Custom scrollbar styles
const customScrollbarStyles = `
  .custom-scrollbar::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }
  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

// ==================== COMPONENTE MANAGER DE ARTÍCULOS ====================
const ArticulosSolicitudManager = ({
  articulos = [],
  onArticulosChange,
  articulosDisponibles = [],
  notify
}) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [articulosActuales, setArticulosActuales] = useState(articulos);
  const [externoNombre, setExternoNombre] = useState('');
  const [externoCantidad, setExternoCantidad] = useState(1);

  // Sincronizar con prop externa
  useEffect(() => {
    setArticulosActuales(articulos);
  }, [articulos]);

  // Preparar opciones para el selector de artículos
  const opcionesArticulos = articulosDisponibles.map(art => ({
    value: art.id,
    label: `${art.nombre} ${art.tipo === 'servicio' ? '(Servicio)' : `(Stock: ${art.stock_actual || 0})`}`,
    tipo: art.tipo,
    stock: art.stock_actual,
    datos: art
  }));

  // Agregar artículo/servicio a la lista
  const handleAgregarArticulo = () => {
    if (!selectedItem) return;

    // Verificar si ya está en la lista
    const yaExiste = articulosActuales.some(
      art => art.id_articulo === selectedItem.value
    );

    if (yaExiste) {
      notify('warning', 'Atención', 'Este artículo/servicio ya está agregado');
      return;
    }

    const nuevoArticulo = {
      id_articulo: selectedItem.value,
      nombre: selectedItem.datos.nombre,
      tipo: selectedItem.tipo,
      stock_actual: selectedItem.stock,
      stock: selectedItem.stock,
      cantidad: 1,
      status: 'solicitud_nueva',
      via: '',
      tiempo_entrega: '',
      observacion: ''
    };

    const nuevosArticulos = [...articulosActuales, nuevoArticulo];
    setArticulosActuales(nuevosArticulos);
    onArticulosChange(nuevosArticulos);
    setSelectedItem(null);
  };

  // ⭐ Actualizar campo de un artículo específico
  const updateArticuloField = (index, field, value) => {
    const nuevosArticulos = [...articulosActuales];
    nuevosArticulos[index] = {
      ...nuevosArticulos[index],
      [field]: value
    };
    setArticulosActuales(nuevosArticulos);
    onArticulosChange(nuevosArticulos);
  };

  // ⭐ Eliminar artículo de la lista
  const removeArticulo = (index) => {
    const nuevosArticulos = articulosActuales.filter((_, i) => i !== index);
    setArticulosActuales(nuevosArticulos);
    onArticulosChange(nuevosArticulos);
  };

  return (
    <div className="space-y-4">
      {/* 🔍 BUSCADOR DE ARTÍCULOS */}
      <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
        <label className="block text-gray-700 font-medium mb-2">
          🔍 Buscar y Seleccionar Artículos/Servicios
        </label>
        <div className="flex gap-2">
          <select
            value={selectedItem ? selectedItem.value : ''}
            onChange={(e) => {
              const opt = opcionesArticulos.find(o => String(o.value) === e.target.value);
              setSelectedItem(opt || null);
            }}
            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">Buscar artículo o servicio...</option>
            {opcionesArticulos.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleAgregarArticulo}
            disabled={!selectedItem}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            ➕ Agregar
          </button>
        </div>
      </div>

      {/* Agregar Artículo Externo (no catalogado) */}
      <div className="border-2 border-orange-200 rounded-lg p-4 bg-orange-50">
        <label className="block text-gray-700 font-medium mb-2">
          Agregar Artículo Externo (no catalogado)
        </label>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <input
              type="text"
              value={externoNombre}
              onChange={(e) => setExternoNombre(e.target.value)}
              placeholder="Nombre del artículo externo..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
            />
          </div>
          <div className="w-24">
            <input
              type="number"
              min="1"
              value={externoCantidad}
              onChange={(e) => setExternoCantidad(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-center"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              const nombre = externoNombre.trim();
              if (!nombre) { notify('warning', 'Atención', 'Ingresa un nombre para el artículo externo'); return; }
              const nuevoExterno = {
                id_articulo: null,
                nombre: nombre,
                tipo: 'articulo_externo',
                stock_actual: 0,
                stock: 0,
                cantidad: externoCantidad,
                status: 'solicitud_nueva',
                via: '',
                tiempo_entrega: '',
                observacion: nombre
              };
              const nuevos = [...articulosActuales, nuevoExterno];
              setArticulosActuales(nuevos);
              onArticulosChange(nuevos);
              setExternoNombre('');
              setExternoCantidad(1);
            }}
            disabled={!externoNombre.trim()}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
          >
            Agregar Externo
          </button>
        </div>
        <p className="text-xs text-orange-600 mt-2">
          Los artículos externos no tienen control de stock. Se crearán automáticamente en el catálogo al guardar la solicitud.
        </p>
      </div>

      {/* LISTA DE ARTÍCULOS USANDO ArticuloItem */}
      {articulosActuales.length > 0 ? (
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-800 flex items-center gap-2">
            <Package size={20} className="text-blue-600" />
            Artículos/Servicios Seleccionados ({articulosActuales.length})
          </h4>
          
          {/* ⭐ AQUÍ SE USA ArticuloItem ⭐ */}
          {articulosActuales.map((articulo, index) => (
            <ArticuloItem
              key={articulo.id_articulo || index}
              articulo={articulo}
              index={index}
              onUpdate={updateArticuloField}
              onRemove={removeArticulo}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Package size={48} className="mx-auto text-gray-400 mb-2" />
          <p className="text-gray-600">No hay artículos o servicios agregados aún.</p>
          <p className="text-sm text-gray-500 mt-1">Usa el buscador de arriba para agregar items.</p>
        </div>
      )}
    </div>
  );
};


const SolicitudesGestionView = () => {
  const { notify, confirm, ModalRenderer } = useModal();
  const { solicitudFilterId, setSolicitudFilterId } = useApp();
  const [solicitudes, setSolicitudes] = useState([]);
  const [solicitudesFiltradas, setSolicitudesFiltradas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState({ estados: {}, vias: [] });

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [categoriaStatusFiltro, setCategoriaStatusFiltro] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('');
  const [empresaFiltro, setEmpresaFiltro] = useState('');
  const [demoradoFiltro, setDemoradoFiltro] = useState('');

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [cotizandoSolicitudId, setCotizandoSolicitudId] = useState(null);
  const [selectedSolicitudIds, setSelectedSolicitudIds] = useState([]);
  const [combinandoSolicitudes, setCombinandoSolicitudes] = useState(false);

  // Ordenamiento
  const [sortColumn, setSortColumn] = useState('fecha_creacion');
  const [sortDirection, setSortDirection] = useState('desc');

  const cargarSolicitudes = async () => {
    try {
      setLoading(true);
      // GET /solicitudes ya trae los articulos embebidos por fila (ver
      // backend, solicitudes.js) -- pedirlos aparte por cada solicitud
      // era un N+1 de requests que no aportaba nada nuevo.
      const response = await solicitudesService.getAll({});
      const listaSolicitudes = response?.data || response || [];
      const solicitudesConArticulos = listaSolicitudes.map((solicitud) => ({
        ...solicitud,
        articulos: Array.isArray(solicitud.articulos) ? solicitud.articulos : []
      }));

      setSolicitudes(solicitudesConArticulos);
      setSolicitudesFiltradas(solicitudesConArticulos);
    } catch (error) {
      console.error('Error al cargar solicitudes:', error);
      await notify('error', 'Error', error.message || 'Error al cargar solicitudes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (solicitudFilterId && solicitudes.length > 0) {
      const filtrada = solicitudes.filter(s => s.id === solicitudFilterId);
      if (filtrada.length > 0) {
        setSolicitudesFiltradas(filtrada);
        setSearchTerm('');
        setStatusFiltro('');
        setCategoriaStatusFiltro('');
        setEmpresaFiltro('');
        setDemoradoFiltro('');
      }
      setSolicitudFilterId(null);
    }
  }, [solicitudFilterId, solicitudes]);

  const isSolicitudEnviadaACotizaciones = (solicitud) => {
    return !!(
      solicitud.cotizacion_creada === true ||
      solicitud.cotizacion_id ||
      solicitud.categoria_status === 'en_curso' ||
      solicitud.categoria_status === 'completado' ||
      solicitud.status === 'cotizada' ||
      solicitud.status === 'cotizacion_generada'
    );
  };

  const handleCrearCotizacionDesdeSolicitud = async (solicitud) => {
    if (!solicitud.articulos || solicitud.articulos.length === 0) {
      await notify('warning', 'Atención', 'No se puede crear cotización: la solicitud no tiene artículos.');
      return;
    }

    if (isSolicitudEnviadaACotizaciones(solicitud)) {
      await notify('warning', 'Atención', 'Esta solicitud ya fue enviada al módulo de cotizaciones y no puede aceptarse de nuevo.');
      return;
    }

    const identificador = solicitud.folio || `#${solicitud.id}`;
    const confirmar = await confirm(
      'Crear cotización',
      `¿Crear cotización para ${identificador}? Se creará una cotización con los artículos de esta solicitud y se registrará en el historial.`
    );
    if (!confirmar) return;

    try {
      setCotizandoSolicitudId(solicitud.id);
      const payload = {
        estado: 'en_proceso',
        registrarHistorial: true,
        historial: [
          ...(solicitud.historial || []),
          {
            tipo: 'cotizacion_creada',
            fecha: new Date().toISOString(),
            origen: solicitud.folio || `SOL-${solicitud.id}`,
            mensaje: 'Solicitud enviada al módulo de cotizaciones',
            usuario: window.currentUser?.nombre || window.user?.nombre || 'Usuario'
          }
        ]
      };

      const apiResponse = await api.post(`/cotizaciones/desde-solicitud/${solicitud.id}`, payload);
      const response = apiResponse.data;

      if (response.success) {
        await notify('success', 'Listo', 'Cotización creada correctamente.');

        const solicitudActualizada = response.solicitud || {
          ...solicitud,
          categoria_status: 'en_curso',
          status: 'cotizada',
          cotizacion_creada: true,
          historial: payload.historial
        };

        setSolicitudes((prev) => prev.map((item) => item.id === solicitud.id ? solicitudActualizada : item));
        await cargarSolicitudes();
        await cargarStats();
      } else {
        await notify('error', 'Error', response.message || 'No se pudo crear la cotización.');
      }
    } catch (error) {
      console.error('Error al crear cotización desde solicitud:', error);
      await notify('error', 'Error', error.response?.data?.message || error.message || 'Error al crear cotización.');
    } finally {
      setCotizandoSolicitudId(null);
    }
  };

  const puedeSeleccionarseParaCotizar = (solicitud) => {
    if (isSolicitudEnviadaACotizaciones(solicitud) || !solicitud.articulos || solicitud.articulos.length === 0) {
      return false;
    }
    // Sin selección previa, o esta misma ya está seleccionada (para poder desmarcarla):
    // no hay restricción de cliente/empresa todavía.
    if (selectedSolicitudIds.length === 0 || selectedSolicitudIds.includes(solicitud.id)) {
      return true;
    }
    // Con una selección en curso, solo se puede sumar otra solicitud del mismo
    // cliente o de la misma empresa que la primera -- son los unicos criterios
    // que combinacionValida acepta para combinar en una sola cotización.
    const primeraSeleccionada = solicitudes.find(s => s.id === selectedSolicitudIds[0]);
    if (!primeraSeleccionada) return true;
    const mismoCliente = primeraSeleccionada.id_cliente && solicitud.id_cliente === primeraSeleccionada.id_cliente;
    const mismaEmpresa = primeraSeleccionada.id_empresa && solicitud.id_empresa === primeraSeleccionada.id_empresa;
    return !!(mismoCliente || mismaEmpresa);
  };

  const toggleSeleccionSolicitud = (solicitud) => {
    if (!puedeSeleccionarseParaCotizar(solicitud)) return;
    setSelectedSolicitudIds(prev =>
      prev.includes(solicitud.id) ? prev.filter(id => id !== solicitud.id) : [...prev, solicitud.id]
    );
  };

  const solicitudesSeleccionadas = solicitudes.filter(s => selectedSolicitudIds.includes(s.id));
  const primeraSeleccionada = solicitudesSeleccionadas[0];
  const mismoClienteSeleccion = !!(primeraSeleccionada?.id_cliente &&
    solicitudesSeleccionadas.every(s => s.id_cliente === primeraSeleccionada.id_cliente));
  const mismaEmpresaSeleccion = !!(primeraSeleccionada?.id_empresa &&
    solicitudesSeleccionadas.every(s => s.id_empresa === primeraSeleccionada.id_empresa));
  const combinacionValida = solicitudesSeleccionadas.length >= 2 && (mismoClienteSeleccion || mismaEmpresaSeleccion);

  const handleCombinarSolicitudes = async () => {
    if (!combinacionValida || combinandoSolicitudes) return;

    const folios = solicitudesSeleccionadas.map(s => s.folio || `#${s.id}`).join(', ');
    const confirmar = await confirm(
      'Combinar solicitudes',
      `¿Crear una sola cotización combinando ${solicitudesSeleccionadas.length} solicitudes (${folios})?`
    );
    if (!confirmar) return;

    try {
      setCombinandoSolicitudes(true);
      const respuesta = await cotizacionesService.crearDesdeSolicitudes(selectedSolicitudIds, { registrarHistorial: true });
      if (respuesta.success) {
        await notify('success', 'Listo', 'Cotización combinada creada correctamente.');
        setSelectedSolicitudIds([]);
        await cargarSolicitudes();
        await cargarStats();
      } else {
        await notify('error', 'Error', respuesta.message || 'No se pudo crear la cotización combinada.');
      }
    } catch (error) {
      console.error('Error al combinar solicitudes:', error);
      await notify('error', 'Error', error.message || 'Error al combinar solicitudes.');
    } finally {
      setCombinandoSolicitudes(false);
    }
  };

  const cargarStats = async () => {
    try {
      const response = await solicitudesService.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
    }
  };

  const cargarConfig = async () => {
  try {
    const response = await solicitudesService.getConfig();
    setConfig(response.data);
  } catch (error) {
    console.error('Error al cargar configuración:', error);
    // Fallback: configuración por defecto
    setConfig({
      estados: {
        pendiente: [
          { value: 'solicitud_nueva', label: 'Solicitud Nueva', color: '#3B82F6' },
          { value: 'pedido_en_espera', label: 'Pedido en Espera', color: '#8B5CF6' }
        ],
        en_curso: [
          { value: 'en_proceso', label: 'En Proceso', color: '#F59E0B' },
          { value: 'anticipo_recibido', label: 'Anticipo Recibido', color: '#06B6D4' },
          { value: 'pendiente_diferencial', label: 'Pendiente por Diferencial', color: '#EC4899' }
        ],
        completado: [
          { value: 'completada', label: 'Completada', color: '#10B981' },
          { value: 'listo_para_facturar', label: 'Listo para Facturar', color: '#14B8A6' }
        ]
      },
      vias: [
        { value: 'personal', label: 'Entrega Personal' },
        { value: 'envio', label: 'Envío' },
        { value: 'recogida', label: 'Recogida en Almacén' }
      ]
    });
  }
};

  useEffect(() => {
    cargarSolicitudes();
    cargarStats();
    cargarConfig();
  }, []);

  useEffect(() => {
    let resultado = [...solicitudes];

    // Filtro por búsqueda
    if (searchTerm) {
      resultado = resultado.filter(s =>
        s.folio.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.nombre_solicitud.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.empresa_nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.cliente_nombre && s.cliente_nombre.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filtro por categoría de status
    if (categoriaStatusFiltro) {
      resultado = resultado.filter(s => s.categoria_status === categoriaStatusFiltro);
    }

    // Filtro por status específico
    if (statusFiltro) {
      resultado = resultado.filter(s => s.status === statusFiltro);
    }

    // Filtro por empresa
    if (empresaFiltro) {
      resultado = resultado.filter(s => s.id_empresa === parseInt(empresaFiltro));
    }

    // Filtro por demorado
    if (demoradoFiltro) {
      resultado = resultado.filter(s => s.demorado === (demoradoFiltro === 'true' ? 1 : 0));
    }

    // Ordenamiento
    resultado.sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    setSolicitudesFiltradas(resultado);
    setCurrentPage(1);
  }, [searchTerm, categoriaStatusFiltro, statusFiltro, empresaFiltro, demoradoFiltro, solicitudes, sortColumn, sortDirection]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = solicitudesFiltradas.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(solicitudesFiltradas.length / itemsPerPage);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleNuevaSolicitud = () => {
    setModalMode('create');
    setSolicitudSeleccionada(null);
    setShowModal(true);
  };

  const handleEditarSolicitud = (solicitud) => {
    setModalMode('edit');
    setSolicitudSeleccionada(solicitud);
    setShowModal(true);
  };

  const handleVerSolicitud = (solicitud) => {
    setModalMode('view');
    setSolicitudSeleccionada(solicitud);
    setShowModal(true);
  };

  const handleEliminarSolicitud = async (id) => {
    const ok = await confirm('Eliminar solicitud', '¿Estás seguro de eliminar esta solicitud?');
    if (ok) {
      try {
        await solicitudesService.delete(id);
        await notify('success', 'Éxito', 'Solicitud eliminada exitosamente');
        cargarSolicitudes();
        cargarStats();
      } catch (error) {
        await notify('error', 'Error', error.message || 'Error al eliminar solicitud');
      }
    }
  };

  const getStatusLabel = (status) => {
    for (const categoria in config.estados) {
      const estado = config.estados[categoria].find(e => e.value === status);
      if (estado) return estado.label;
    }
    return status;
  };

  const getStatusColor = (status) => {
    for (const categoria in config.estados) {
      const estado = config.estados[categoria].find(e => e.value === status);
      if (estado) return estado.color;
    }
    return '#6B7280';
  };

  const getCategoriaStatusColor = (categoria) => {
    const colores = {
      pendiente: 'bg-yellow-100 text-yellow-800',
      en_curso: 'bg-blue-100 text-blue-800',
      completado: 'bg-green-100 text-green-800'
    };
    return colores[categoria] || 'bg-gray-100 text-gray-800';
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  // Obtener lista única de empresas para el filtro
  const empresasUnicas = [...new Map(solicitudes.map(s => [s.id_empresa, { id: s.id_empresa, nombre: s.empresa_nombre }])).values()];

  return (
    <div>
      {/* Custom scrollbar styles */}
      <style>{customScrollbarStyles}</style>

       {/* Header responsivo */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Solicitudes</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {solicitudesFiltradas.length} de {solicitudes.length} solicitudes
          </p>
        </div>
        <button
          onClick={handleNuevaSolicitud}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors w-full sm:w-auto"
        >
          <Plus size={16} /> Nueva solicitud
        </button>
      </div>

      {/* Estadísticas */}
      {stats && (
        <div className="flex flex-wrap gap-x-8 gap-y-3 mb-6 pb-6 border-b border-slate-200">
          {[
            { label: 'Total', value: stats.total, color: 'text-slate-900' },
            { label: 'Pendientes', value: stats.pendientes, color: 'text-amber-600' },
            { label: 'En curso', value: stats.en_curso, color: 'text-brand-600' },
            { label: 'Completadas', value: stats.completadas, color: 'text-emerald-600' },
            { label: 'Demoradas', value: stats.demoradas, color: 'text-red-600' },
          ].map((item) => (
            <div key={item.label}>
              <p className={`text-2xl font-semibold ${item.color}`}>{item.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{item.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filtros */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2 mb-4">
        <input
          type="text"
          placeholder="Buscar por folio, nombre, empresa..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
        />
        <select
          value={categoriaStatusFiltro}
          onChange={(e) => setCategoriaStatusFiltro(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
        >
          <option value="">Todas las categorías</option>
          <option value="pendiente">Pendiente</option>
          <option value="en_curso">En Curso</option>
          <option value="completado">Completado</option>
        </select>
        <select
          value={statusFiltro}
          onChange={(e) => setStatusFiltro(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
        >
          <option value="">Todos los estados</option>
          {Object.entries(config.estados).map(([categoria, estados]) => (
            <optgroup key={categoria} label={categoria.toUpperCase()}>
              {estados.map(estado => (
                <option key={estado.value} value={estado.value}>{estado.label}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <select
          value={empresaFiltro}
          onChange={(e) => setEmpresaFiltro(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
        >
          <option value="">Todas las empresas</option>
          {empresasUnicas.map(emp => (
            <option key={emp.id} value={emp.id}>{emp.nombre}</option>
          ))}
        </select>
        <select
          value={demoradoFiltro}
          onChange={(e) => setDemoradoFiltro(e.target.value)}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
        >
          <option value="">Demoradas/No Demoradas</option>
          <option value="true">Solo Demoradas</option>
          <option value="false">No Demoradas</option>
        </select>
        <select
          value={itemsPerPage}
          onChange={(e) => setItemsPerPage(Number(e.target.value))}
          className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
        >
          <option value={10}>10 por página</option>
          <option value={25}>25 por página</option>
          <option value={50}>50 por página</option>
          <option value={100}>100 por página</option>
        </select>
      </div>

      {/* Barra de acción masiva: combinar solicitudes seleccionadas en una cotización */}
      {selectedSolicitudIds.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 border-l-2 border-l-brand-600 rounded-lg p-4 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-sm text-slate-700">
            <strong>{selectedSolicitudIds.length}</strong> solicitud(es) seleccionada(s)
            {selectedSolicitudIds.length >= 2 && !combinacionValida && (
              <p className="text-xs text-red-600 mt-1">
                Deben pertenecer todas al mismo cliente o a la misma empresa para combinarse.
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedSolicitudIds([])}
              className="px-3 py-2 text-sm border border-slate-300 rounded-lg hover:bg-slate-100"
            >
              Limpiar selección
            </button>
            <button
              onClick={handleCombinarSolicitudes}
              disabled={!combinacionValida || combinandoSolicitudes}
              className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {combinandoSolicitudes ? <Loader2 size={16} className="animate-spin" /> : <ShoppingCart size={16} />}
              Combinar en una cotización
            </button>
          </div>
        </div>
      )}

        {/* Vista de tabla compacta: una linea por solicitud. El detalle
            completo (articulos, opciones de compra, historial) sigue
            disponible en el modal de "Ver detalles" -- no hace falta
            duplicarlo aqui como antes con las tarjetas expandibles. */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-600">
            <tr>
              <th className="px-3 py-2 w-8"></th>
              <th className="px-3 py-2">Folio</th>
              <th className="px-3 py-2">De qué trata</th>
              <th className="px-3 py-2">Cliente / Empresa</th>
              <th className="px-3 py-2">Servicio</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2 text-center">Ítems</th>
              <th className="px-3 py-2">Fecha</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {currentItems.map(solicitud => {
              const combinable = puedeSeleccionarseParaCotizar(solicitud);
              const seleccionada = selectedSolicitudIds.includes(solicitud.id);
              const descripcionCorta = solicitud.nombre_solicitud || solicitud.descripcion || 'Sin descripción';

              return (
                <tr key={solicitud.id} className={`hover:bg-gray-50 ${seleccionada ? 'bg-amber-50' : ''}`}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={seleccionada}
                      onChange={() => toggleSeleccionSolicitud(solicitud)}
                      disabled={!combinable}
                      title={combinable ? 'Seleccionar para combinar en una cotización' : 'No se puede combinar: sin artículos o ya enviada a cotizaciones'}
                      className="w-4 h-4 disabled:opacity-30"
                    />
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      {solicitud.demorado === 1 && (
                        <AlertCircle size={14} className="text-red-500 flex-shrink-0" title="Solicitud demorada" />
                      )}
                      <button onClick={() => handleVerSolicitud(solicitud)} className="font-bold text-blue-600 hover:underline">
                        {solicitud.folio}
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2 max-w-xs">
                    <p className="truncate text-gray-800" title={descripcionCorta}>{descripcionCorta}</p>
                  </td>
                  <td className="px-3 py-2 max-w-[10rem]">
                    <p className="truncate text-gray-700" title={solicitud.empresa_nombre || solicitud.cliente_nombre || ''}>
                      {solicitud.empresa_nombre || solicitud.cliente_nombre || <span className="text-gray-400 italic">Sin cliente</span>}
                    </p>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold text-white" style={{ backgroundColor: solicitud.servicio_color }}>
                      {solicitud.servicio_nombre}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${getCategoriaStatusColor(solicitud.categoria_status)}`}>
                      {solicitud.categoria_status === 'pendiente' && 'Pendiente'}
                      {solicitud.categoria_status === 'en_curso' && 'En Curso'}
                      {solicitud.categoria_status === 'completado' && 'Completado'}
                    </span>
                    {isSolicitudEnviadaACotizaciones(solicitud) && (
                      <span className="block text-[10px] text-slate-500 mt-0.5">Enviada a cotizaciones</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center text-gray-600">
                    {solicitud.articulos?.length || 0}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-xs">
                    {new Date(solicitud.fecha_creacion).toLocaleDateString('es-ES')}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => handleVerSolicitud(solicitud)} className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 p-1.5 rounded-lg transition-colors" title="Ver detalles">
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => handleCrearCotizacionDesdeSolicitud(solicitud)}
                        disabled={
                          cotizandoSolicitudId === solicitud.id ||
                          !solicitud.articulos ||
                          solicitud.articulos.length === 0 ||
                          isSolicitudEnviadaACotizaciones(solicitud)
                        }
                        className="text-amber-600 hover:text-amber-800 hover:bg-amber-100 p-1.5 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title={
                          !solicitud.articulos || solicitud.articulos.length === 0
                            ? 'La solicitud no tiene artículos'
                            : isSolicitudEnviadaACotizaciones(solicitud)
                            ? 'Esta solicitud ya fue enviada al módulo de cotizaciones'
                            : 'Crear cotización'
                        }
                      >
                        {cotizandoSolicitudId === solicitud.id ? <Loader2 size={16} className="animate-spin" /> : <ShoppingCart size={16} />}
                      </button>
                      <button onClick={() => handleEditarSolicitud(solicitud)} className="text-green-600 hover:text-green-800 hover:bg-green-100 p-1.5 rounded-lg transition-colors" title="Editar">
                        <Edit size={16} />
                      </button>
                      <button onClick={() => handleEliminarSolicitud(solicitud.id)} className="text-red-600 hover:text-red-800 hover:bg-red-100 p-1.5 rounded-lg transition-colors" title="Eliminar">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

       {/* Paginación responsiva */}
      <div className="border-t border-slate-200 mt-4 pt-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-sm text-slate-500 text-center sm:text-left">
            Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, solicitudesFiltradas.length)} de {solicitudesFiltradas.length}
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded hover:bg-slate-100 disabled:opacity-50 text-sm"
            >
              Primero
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded hover:bg-slate-100 disabled:opacity-50 text-sm"
            >
              Ant.
            </button>
            <span className="px-3 py-1 text-sm">
              {currentPage} / {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded hover:bg-slate-100 disabled:opacity-50 text-sm"
            >
              Sig.
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded hover:bg-slate-100 disabled:opacity-50 text-sm"
            >
              Último
            </button>
          </div>
        </div>
      </div>


      {/* Modal */}
      {showModal && (
        <SolicitudModal
          mode={modalMode}
          solicitud={solicitudSeleccionada}
          config={config}
          onClose={() => {
            setShowModal(false);
            setSolicitudSeleccionada(null);
          }}
          onSuccess={() => {
            setShowModal(false);
            setSolicitudSeleccionada(null);
            cargarSolicitudes();
            cargarStats();
          }}
        />
      )}
      {ModalRenderer}
    </div>
  );
};


// ==================== MODAL DE SOLICITUD ====================
const SolicitudModal = ({ mode, solicitud, config, onClose, onSuccess }) => {
  const { notify, confirm, ModalRenderer } = useModal();
      const [formData, setFormData] = useState({
      nombre_solicitud: '',
      descripcion: '',
      id_servicio: '',
      id_subservicio: '',
      id_categoria: '',
      articulos_seleccionados: [], // ← CAMBIAR de id_articulo a esto
      id_empresa: '',
      id_cliente: '',
      id_usuario_asignado: ''
    });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Datos para selects
  const [servicios, setServicios] = useState([]);
  const [subservicios, setSubservicios] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [articulos, setArticulos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [stockArticulo, setStockArticulo] = useState(null);
  const [cargandoStock, setCargandoStock] = useState(false);

  // Manejo de archivos
  const [archivos, setArchivos] = useState([]);
  const [archivosSubidos, setArchivosSubidos] = useState([]);
  const [subiendoArchivo, setSubiendoArchivo] = useState(false);

  const [articulosConCantidad, setArticulosConCantidad] = useState([]);
  // Mantener ref sincronizado con el estado
  useEffect(() => {
    articulosConCantidadRef.current = articulosConCantidad;
  }, [articulosConCantidad]);
  const [cargandoArticulos, setCargandoArticulos] = useState(false);
  const [errorArticulos, setErrorArticulos] = useState('');
  const [articulosCargadosUnaVez, setArticulosCargadosUnaVez] = useState(false);
  const selectArticulosRef = useRef(null);
  // Ref para evitar stale closure en handleArticulosChange
  const articulosConCantidadRef = useRef([]);

  // ⭐ DEFINIR isViewMode AL INICIO
  const isViewMode = mode === 'view';

  // ✅ CARGAR DATOS INICIALES (una sola vez)
  useEffect(() => {
    cargarDatosIniciales();
  }, []);

  // ✅ CARGAR DATOS DE SOLICITUD + ARTÍCULOS (una sola función, sin duplicados)
  useEffect(() => {
    if (!solicitud || !(mode === 'edit' || mode === 'view')) return;

    // Datos básicos del formulario
    setFormData({
      nombre_solicitud: solicitud.nombre_solicitud || '',
      descripcion: solicitud.descripcion || '',
      id_servicio: solicitud.id_servicio || '',
      id_subservicio: solicitud.id_subservicio || '',
      id_categoria: solicitud.id_categoria || '',
      id_empresa: solicitud.id_empresa || '',
      id_cliente: solicitud.id_cliente || '',
      status: solicitud.status || 'solicitud_nueva',
      via: solicitud.via || '',
      tiempo_entrega: solicitud.tiempo_entrega ? solicitud.tiempo_entrega.split('T')[0] : '',
      id_usuario_asignado: solicitud.id_usuario_asignado || ''
    });

    cargarArchivos(solicitud.id);
    cargarArticulosDeSolicitud(solicitud.id, mode);
  }, [solicitud, mode]);

  // FUNCIÓN para cargar artículos con soporte para externos y manejo de errores
  const cargarArticulosDeSolicitud = async (solicitudId, modoActual) => {
    setCargandoArticulos(true);
    setErrorArticulos('');
    try {
      const response = await solicitudesService.getArticulos(solicitudId);
      const rawData = response?.data || response || [];
      const listaArticulos = Array.isArray(rawData) ? rawData : [];
      console.log('GET articulos respuesta:', listaArticulos);

      const articulosCargados = listaArticulos.map(item => {
        const viaValor = (item.via !== null && item.via !== undefined) ? String(item.via).trim() : '';
        const esExterno = !item.id_articulo;
        return {
          id_articulo: item.id_articulo,
          nombre:       item.nombre || (esExterno ? (item.observacion || 'Articulo externo') : ''),
          tipo:         item.tipo || (esExterno ? 'articulo_externo' : 'articulo'),
          cantidad:     item.cantidad || 1,
          stock:        item.stock_actual || 0,
          stock_minimo: item.stock_minimo || 0,
          status:       item.status || 'solicitud_nueva',
          via:          viaValor,
          tiempo_entrega: item.tiempo_entrega ? item.tiempo_entrega.split('T')[0] : '',
          observacion:  item.observacion || ''
        };
      });

      console.log('articulosCargados FINAL:', articulosCargados.length, 'items');

      articulosConCantidadRef.current = articulosCargados;
      setArticulosConCantidad(articulosCargados);
      setArticulosCargadosUnaVez(true);

      if (modoActual === 'edit') {
        setTimeout(() => {
          const $select = $(selectArticulosRef.current);
          if ($select.data('select2')) {
            const ids = articulosCargados.map(a => String(a.id_articulo)).filter(Boolean);
            $select.off('change');
            if (ids.length > 0) {
              $select.val(ids).trigger('change.select2');
            }
            $select.on('change', function () {
              handleArticulosChange($(this).val() || []);
            });
          }
        }, 200);
      }
    } catch (error) {
      console.error('Error al cargar articulos:', error);
      setErrorArticulos('No se pudieron cargar los articulos. ' + (error.message || ''));
      setArticulosCargadosUnaVez(true);
    } finally {
      setCargandoArticulos(false);
    }
  };

  // ⭐ NUEVA función para cargar stock
  const cargarStockArticulo = async (articuloId) => {
    setCargandoStock(true);
    try {
      const response = await articulosService.getStock(articuloId);
      setStockArticulo(response.data);
    } catch (error) {
      console.error('Error al cargar stock:', error);
      setStockArticulo(null);
    } finally {
      setCargandoStock(false);
    }
  };

  const cargarDatosIniciales = async () => {
    try {
      const [
        resServicios,
        resSubservicios,
        resCategorias,
        resArticulos,
        resEmpresas,
        resClientes,
        resUsuarios
      ] = await Promise.all([
        serviciosService.getAll({ activo: true }),
        subserviciosService.getAll({ activo: true }),
        categoriasSolicitudService.getAll({ activo: true }),
        articulosService.getAll({}),
        empresasService.getAll({ activo: true }),
        clientesService.getAll({ activo: true }),
        usuariosService.getAll()
      ]);
      
      setServicios(resServicios.data);
      setSubservicios(resSubservicios.data);
      setCategorias(resCategorias.data);
      setArticulos(resArticulos.data);
      setEmpresas(resEmpresas.data);
      setClientes(resClientes.data);
      setUsuarios(resUsuarios.data);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    }
  };

  const cargarArchivos = async (solicitudId) => {
    try {
      const response = await solicitudesArchivosService.getBySolicitudId(solicitudId);
      setArchivosSubidos(response.data);
    } catch (error) {
      console.error('Error al cargar archivos:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);

    if (archivosSubidos.length + archivos.length + files.length > 3) {
      await notify('warning', 'Atención', 'Solo se permiten máximo 3 archivos');
      return;
    }

    const archivosValidos = [];
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        await notify('warning', 'Archivo muy grande', `El archivo ${file.name} supera los 5MB`);
        continue;
      }
      archivosValidos.push(file);
    }

    setArchivos(prev => [...prev, ...archivosValidos]);
  };

  const eliminarArchivo = (index) => {
    setArchivos(prev => prev.filter((_, i) => i !== index));
  };

  const eliminarArchivoSubido = async (archivoId) => {
    const ok = await confirm('Eliminar archivo', '¿Eliminar este archivo?');
    if (!ok) return;

    try {
      await solicitudesArchivosService.delete(archivoId);
      setArchivosSubidos(prev => prev.filter(a => a.id !== archivoId));
      await notify('success', 'Éxito', 'Archivo eliminado');
    } catch (error) {
      await notify('error', 'Error', 'Error al eliminar archivo');
    }
  };

  const subirArchivos = async (solicitudId) => {
    if (archivos.length === 0) return;

    setSubiendoArchivo(true);
    
    try {
      for (const archivo of archivos) {
        await solicitudesArchivosService.upload(solicitudId, archivo);
      }
      setArchivos([]);
    } catch (error) {
      console.error('Error al subir archivos:', error);
      throw error;
    } finally {
      setSubiendoArchivo(false);
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');

  if (!formData.nombre_solicitud.trim()) {
    setError('El nombre de la solicitud es requerido');
    setLoading(false);
    return;
  }

  if (!formData.id_servicio || !formData.id_categoria || !formData.id_empresa) {
    setError('Servicio, Categoría y Empresa son requeridos');
    setLoading(false);
    return;
  }

  try {
    // Auto-crear artículos externos (id_articulo null) en el catálogo
    let articulosParaEnviar = [...articulosConCantidad];
    const articulosExternos = articulosConCantidad.filter(a => !a.id_articulo);
    
    if (articulosExternos.length > 0) {
      for (let i = 0; i < articulosConCantidad.length; i++) {
        const art = articulosConCantidad[i];
        if (!art.id_articulo) {
          try {
            const nombreArt = art.nombre || art.observacion || 'Artículo externo';
            if (nombreArt && nombreArt !== 'Artículo externo') {
              const nuevoArticulo = await articulosService.create({
                nombre: nombreArt,
                tipo: 'articulo',
                stock_actual: 0,
                stock_minimo: 0,
                precio_venta: 0,
                activo: true
              });
              const nuevoId = nuevoArticulo?.data?.id || nuevoArticulo?.id;
              if (nuevoId) {
                articulosParaEnviar[i] = { ...art, id_articulo: nuevoId };
              }
            }
          } catch (err) {
            console.warn('No se pudo crear artículo externo, se envía sin id_articulo:', err);
          }
        }
      }
      setArticulosConCantidad(articulosParaEnviar);
    }

    const dataToSend = {
      ...formData,
      id_subservicio: formData.id_subservicio || null,
      articulos: articulosParaEnviar,
      id_cliente: formData.id_cliente || null,
      via: formData.via || null,
      tiempo_entrega: formData.tiempo_entrega || null,
      id_usuario_asignado: formData.id_usuario_asignado || null
    };

    let solicitudId;

    if (mode === 'create') {
      const response = await solicitudesService.create(dataToSend);
      solicitudId = response.data.id;
      
      if (archivos.length > 0) {
        await subirArchivos(solicitudId);
      }
      
      await notify('success', 'Éxito', `Solicitud creada exitosamente\nFolio: ${response.data.folio}`);
    } else if (mode === 'edit') {
      await solicitudesService.update(solicitud.id, dataToSend);
      solicitudId = solicitud.id;

      if (archivos.length > 0) {
        await subirArchivos(solicitudId);
      }

      await notify('success', 'Éxito', 'Solicitud actualizada exitosamente');
    }
    
    onSuccess();
  } catch (err) {
    setError(err.response?.data?.message || err.message || 'Error al guardar solicitud');
  } finally {
    setLoading(false);
  }
};

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (tipoArchivo, nombreArchivo) => {
    const extension = nombreArchivo.split('.').pop().toLowerCase();
    
    if (tipoArchivo?.includes('pdf') || extension === 'pdf') {
      return { icon: FileText, color: 'text-red-600', bg: 'bg-red-50', label: 'PDF' };
    }
    
    if (tipoArchivo?.includes('word') || extension === 'doc' || extension === 'docx') {
      return { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50', label: 'WORD' };
    }
    
    if (tipoArchivo?.includes('sheet') || extension === 'xls' || extension === 'xlsx') {
      return { icon: FileText, color: 'text-green-600', bg: 'bg-green-50', label: 'EXCEL' };
    }
    
    return { icon: FileText, color: 'text-gray-600', bg: 'bg-gray-50', label: extension.toUpperCase() };
  };

    useEffect(() => {
      if (!isViewMode && articulos.length > 0) {
        const $select = $(selectArticulosRef.current);

        if ($select.data('select2')) {
          $select.select2('destroy');
        }

        $select.select2({
          placeholder: 'Buscar y seleccionar artículos...',
          allowClear: true,
          width: '100%',
          language: {
            noResults: () => 'No se encontraron artículos',
            searching: () => 'Buscando...'
          }
        });

        const articulosPreCargados = articulosConCantidadRef.current;
        if (articulosPreCargados.length > 0) {
          const ids = articulosPreCargados.map(a => String(a.id_articulo)).filter(Boolean);
          if (ids.length > 0) {
            $select.val(ids);
          }
        }

        $select.on('change', function() {
          const selectedIds = $(this).val() || [];
          handleArticulosChange(selectedIds);
        });

        return () => {
          if ($select.data('select2')) {
            $select.select2('destroy');
          }
        };
      }
    }, [articulos, isViewMode]);

  const handleArticulosChange = useCallback((selectedIds) => {
  // ⭐ Usar ref para evitar stale closure — siempre tiene el valor más reciente
  const currentArticulos = articulosConCantidadRef.current;
  
  const nuevosArticulos = selectedIds.map(id => {
    const articulo = articulos.find(a => a.id === parseInt(id));
    const articuloExistente = currentArticulos.find(a => a.id_articulo === parseInt(id));
    
    return {
      id_articulo: parseInt(id),
      nombre: articulo?.nombre || '',
      tipo: articulo?.tipo || 'articulo',
      cantidad: articuloExistente?.cantidad || 1,
      stock: articulo?.stock_actual || 0,
      stock_minimo: articulo?.stock_minimo || 0,
      // Preservar campos editados por el usuario
      status: articuloExistente?.status || 'solicitud_nueva',
      via: articuloExistente?.via || '',
      tiempo_entrega: articuloExistente?.tiempo_entrega || '',
      observacion: articuloExistente?.observacion || ''
    };
  });
  
  setArticulosConCantidad(nuevosArticulos);
}, [articulos]);

  const handleCantidadChange = (id_articulo, cantidad) => {
    setArticulosConCantidad(prev =>
      prev.map(art =>
        art.id_articulo === id_articulo
          ? { ...art, cantidad: parseInt(cantidad) || 1 }
          : art
      )
    );
  };

  const eliminarArticulo = (id_articulo) => {
    setArticulosConCantidad(prev => prev.filter(a => a.id_articulo !== id_articulo));
    
    // Actualizar Select2
    const $select = $(selectArticulosRef.current);
    const currentValues = $select.val() || [];
    const newValues = currentValues.filter(id => parseInt(id) !== id_articulo);
    $select.val(newValues).trigger('change');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-800">
              {mode === 'create' && '📝 Nueva Solicitud'}
              {mode === 'edit' && '✏️ Editar Solicitud'}
              {mode === 'view' && '👁️ Detalles de la Solicitud'}
            </h3>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <span className="text-2xl">×</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Folio */}
          {solicitud && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2">
                <FileText size={24} className="text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Folio</p>
                  <p className="text-xl font-bold text-blue-600">{solicitud.folio}</p>
                </div>
              </div>
            </div>
          )}

          {/* Resumen Financiero de Anticipo (Solo vista mode) */}
          {isViewMode && solicitud?.requiere_anticipo && (
            <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border-2 border-purple-200">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign size={20} className="text-purple-600" />
                <h4 className="font-bold text-purple-800">Resumen de Anticipo</h4>
                <span className="ml-auto text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full font-medium">
                  Congelado
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-purple-600 font-medium mb-1">Monto USD</p>
                  <p className="text-lg font-bold text-purple-900">
                    ${solicitud.anticipo_monto_usd?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-purple-600 font-medium mb-1">Tasa Cambio</p>
                  <p className="text-lg font-bold text-purple-900">
                    {solicitud.anticipo_tasa_cambio?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-purple-600 font-medium mb-1">Equivalente Bs</p>
                  <p className="text-lg font-bold text-purple-900">
                    Bs. {((solicitud.anticipo_monto_usd || 0) * (solicitud.anticipo_tasa_cambio || 0)).toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-purple-600 font-medium mb-1">Fecha Anticipo</p>
                  <p className="text-sm font-semibold text-purple-900">
                    {solicitud.anticipo_fecha ? new Date(solicitud.anticipo_fecha).toLocaleDateString('es-ES') : 'Pendiente'}
                  </p>
                </div>
              </div>
              {solicitud.anticipo_fecha && (
                <div className="mt-3 pt-3 border-t border-purple-200">
                  <p className="text-xs text-purple-600">
                    <Clock size={12} className="inline mr-1" />
                    Tasa histórica congelada al momento del pago
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="space-y-4">
            {/* Nombre de la solicitud */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Nombre de la Solicitud <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="nombre_solicitud"
                value={formData.nombre_solicitud}
                onChange={handleChange}
                disabled={isViewMode}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Ej: Compra de repuestos para mantenimiento"
              />
            </div>

            {/* Fila 1: Servicio, Subservicio, Categoría */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* (mantener igual) */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Servicio <span className="text-red-500">*</span>
                </label>
                <select
                  name="id_servicio"
                  value={formData.id_servicio}
                  onChange={handleChange}
                  disabled={isViewMode}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Seleccionar...</option>
                  {servicios.map(srv => (
                    <option key={srv.id} value={srv.id}>{srv.nombre}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Subservicio
                </label>
                <select
                  name="id_subservicio"
                  value={formData.id_subservicio}
                  onChange={handleChange}
                  disabled={isViewMode}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Ninguno</option>
                  {subservicios.map(sub => (
                    <option key={sub.id} value={sub.id}>{sub.nombre}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Categoría <span className="text-red-500">*</span>
                </label>
                <select
                  name="id_categoria"
                  value={formData.id_categoria}
                  onChange={handleChange}
                  disabled={isViewMode}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Seleccionar...</option>
                  {categorias.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                  ))}
                </select>
            </div>
          </div>

        {/* Fila 2: Empresa, Cliente */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Empresa */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">
            Empresa/Zona <span className="text-red-500">*</span>
          </label>
          <select
            name="id_empresa"
            value={formData.id_empresa}
            onChange={handleChange}
            disabled={isViewMode}
            required
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          >
            <option value="">Seleccionar...</option>
            {empresas.map(emp => (
              <option key={emp.id} value={emp.id}>{emp.nombre}</option>
            ))}
          </select>
        </div>

        {/* Cliente */}
        <div>
          <label className="block text-gray-700 font-medium mb-2">
            Cliente (Opcional)
          </label>
          <select
            name="id_cliente"
            value={formData.id_cliente}
            onChange={handleChange}
            disabled={isViewMode}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          >
            <option value="">Ninguno</option>
            {clientes.map(cli => (
              <option key={cli.id} value={cli.id}>{cli.nombre}</option>
            ))}
          </select>
        </div>
      </div>

{/* ========== GESTIÓN DE ARTÍCULOS/SERVICIOS ========== */}
{!isViewMode && (
  <ArticulosSolicitudManager
    articulos={articulosConCantidad}
    onArticulosChange={setArticulosConCantidad}
    articulosDisponibles={articulos}
    notify={notify}
  />
)}

{/* Error al cargar artículos */}
{errorArticulos && (
  <div className="mt-4 p-4 bg-red-100 border-2 border-red-300 rounded-lg">
    <div className="flex items-center gap-3">
      <AlertCircle size={24} className="text-red-600 flex-shrink-0" />
      <div>
        <p className="font-semibold text-red-800">Error al cargar artículos</p>
        <p className="text-sm text-red-600">{errorArticulos}</p>
      </div>
    </div>
  </div>
)}

{/* ========== VISTA DE ARTÍCULOS CON IMÁGENES (MODO VIEW) ========== */}
{isViewMode && articulosConCantidad.length > 0 && (
  <div className="space-y-4 border-t pt-4 mt-4">
    <h4 className="font-semibold text-xl text-gray-800 flex items-center gap-2">
      <Package size={24} className="text-blue-600" />
      Artículos/Servicios en esta Solicitud ({articulosConCantidad.length})
    </h4>
    
    {cargandoArticulos ? (
      <div className="text-center py-8">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
        <p className="text-gray-600 mt-3">Cargando artículos...</p>
      </div>
    ) : (
      <div className="space-y-4">
        {articulosConCantidad.map((articulo, index) => (
          <ArticuloItem
            key={articulo.id_articulo || index}
            articulo={articulo}
            index={index}
            isViewMode={true}
          />
        ))}
      </div>
    )}
  </div>
)}

{/* Mensaje si no hay artículos (solo cuando ya se intentó cargar) */}
{isViewMode && articulosConCantidad.length === 0 && !cargandoArticulos && !errorArticulos && articulosCargadosUnaVez && (
  <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 mt-4">
    <Package size={64} className="mx-auto text-gray-400 mb-4" />
    <p className="text-gray-600 font-semibold text-lg">No hay artículos en esta solicitud</p>
    <p className="text-sm text-gray-500 mt-2">Esta solicitud aún no tiene artículos o servicios agregados.</p>
  </div>
)}

            {/* ⭐ INDICADOR DE STOCK EN TIEMPO REAL */}
            {formData.id_articulo && stockArticulo && (
              <div className={`p-4 rounded-lg border-2 ${
                formData.cantidad > stockArticulo.stock 
                  ? 'bg-red-50 border-red-300' 
                  : formData.cantidad > (stockArticulo.stock - stockArticulo.stock_minimo)
                  ? 'bg-yellow-50 border-yellow-300'
                  : 'bg-green-50 border-green-300'
              }`}>
                <div className="flex items-center gap-3">
                  <Package size={24} className={
                    formData.cantidad > stockArticulo.stock 
                      ? 'text-red-600' 
                      : formData.cantidad > (stockArticulo.stock - stockArticulo.stock_minimo)
                      ? 'text-yellow-600'
                      : 'text-green-600'
                  } />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">
                      {stockArticulo.nombre}
                    </p>
                    <div className="flex gap-4 mt-1 text-sm">
                      <span className="text-gray-600">
                        Stock disponible: <strong className={
                          stockArticulo.stock === 0 ? 'text-red-600' : 
                          stockArticulo.stock <= stockArticulo.stock_minimo ? 'text-yellow-600' : 
                          'text-green-600'
                        }>{stockArticulo.articulo_stock}</strong>
                      </span>
                      <span className="text-gray-600">
                        Solicitando: <strong className="text-blue-600">{formData.cantidad}</strong>
                      </span>
                      <span className="text-gray-600">
                        Faltante: <strong className={
                          (formData.cantidad - stockArticulo.stock) > 0 ? 'text-red-600' : 'text-green-600'
                        }>
                          {Math.max(0, formData.cantidad - stockArticulo.stock)}
                        </strong>
                      </span>
                    </div>
                  </div>
                  {formData.cantidad > stockArticulo.stock && (
                    <div className="text-right">
                      <AlertCircle size={24} className="text-red-600" />
                      <p className="text-xs text-red-600 font-semibold mt-1">Stock insuficiente</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {cargandoStock && formData.id_articulo && (
              <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                <p className="text-blue-600 text-center">Cargando stock...</p>
              </div>
            )}

           
            {/* Usuario Asignado */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                👤 Asignar a Usuario
              </label>
              <select
                name="id_usuario_asignado"
                value={formData.id_usuario_asignado}
                onChange={handleChange}
                disabled={isViewMode}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="">Sin asignar</option>
                {usuarios.map(usr => (
                  <option key={usr.id} value={usr.id}>
                    {usr.nombre} {usr.email ? `(${usr.email})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                Observación / Lo que están solicitando
              </label>
              <textarea
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                disabled={isViewMode}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Describe detalladamente la solicitud..."
              />
            </div>

            {/* Archivos Adjuntos */}
            <div className="border-t pt-4">
              <label className="block text-gray-700 font-medium mb-2">
                📎 Archivos Adjuntos (Máximo 3)
              </label>
              
              {/* (mantener el código de archivos igual que antes) */}
              {archivosSubidos.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Archivos actuales:</p>
                  <div className="grid grid-cols-3 gap-4">
                    {archivosSubidos.map(archivo => {
                      const esImagen = archivo.tipo_archivo?.startsWith('image/');
                      const urlArchivo = `${UPLOADS_BASE_URL}/uploads/solicitudes/${archivo.ruta_archivo}`;
                      const fileInfo = getFileIcon(archivo.tipo_archivo, archivo.nombre_archivo);
                      const IconComponent = fileInfo.icon;
                      
                      return (
                        <div key={archivo.id} className="relative group">
                          <div className="rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-500 transition">
                            {esImagen ? (
                              <div className="aspect-square">
                                <img
                                  src={urlArchivo}
                                  alt={archivo.nombre_archivo}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className={`aspect-square ${fileInfo.bg} flex flex-col items-center justify-center p-4`}>
                                <IconComponent size={48} className={fileInfo.color} />
                                <span className={`text-xs font-bold ${fileInfo.color} mt-2`}>
                                  {fileInfo.label}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="mt-2">
                            <p className="text-sm font-semibold text-gray-800 truncate" title={archivo.nombre_archivo}>
                              {archivo.nombre_archivo}
                            </p>
                            <p className="text-xs text-gray-500">{formatFileSize(archivo.tamanio)}</p>
                          </div>
                          
                          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                            <a
                              href={urlArchivo}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-blue-600 text-white p-1.5 rounded hover:bg-blue-700 shadow-lg"
                              title="Ver/Descargar">
                            
                              <Eye size={14} />
                            </a>
                            {!isViewMode && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  eliminarArchivoSubido(archivo.id);
                                }}
                                className="bg-red-600 text-white p-1.5 rounded hover:bg-red-700 shadow-lg"
                                title="Eliminar"
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {archivos.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Archivos por subir:</p>
                  <div className="grid grid-cols-3 gap-4">
                    {archivos.map((archivo, index) => {
                      const esImagen = archivo.type?.startsWith('image/');
                      const previewUrl = esImagen ? URL.createObjectURL(archivo) : null;
                      const fileInfo = getFileIcon(archivo.type, archivo.name);
                      const IconComponent = fileInfo.icon;
                      
                      return (
                        <div key={index} className="relative group">
                          <div className="rounded-lg overflow-hidden border-2 border-yellow-400">
                            {esImagen ? (
                              <div className="aspect-square">
                                <img
                                  src={previewUrl}
                                  alt={archivo.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className={`aspect-square ${fileInfo.bg} flex flex-col items-center justify-center p-4`}>
                                <IconComponent size={48} className={fileInfo.color} />
                                <span className={`text-xs font-bold ${fileInfo.color} mt-2`}>
                                  {fileInfo.label}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="mt-2">
                            <p className="text-sm font-semibold text-gray-800 truncate" title={archivo.name}>
                              {archivo.name}
                            </p>
                            <p className="text-xs text-gray-500">{formatFileSize(archivo.size)}</p>
                          </div>
                          
                          {!isViewMode && (
                            <button
                              type="button"
                              onClick={() => eliminarArchivo(index)}
                              className="absolute top-2 right-2 bg-red-600 text-white p-1.5 rounded hover:bg-red-700 shadow-lg opacity-0 group-hover:opacity-100 transition"
                              title="Eliminar"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {!isViewMode && (archivosSubidos.length + archivos.length < 3) && (
                <div>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    multiple
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Formatos: Imágenes, PDF, Word, Excel. Máximo 5MB por archivo.
                  </p>
                </div>
              )}
            </div>

            {/* Info adicional en modo vista */}
            {isViewMode && solicitud && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <Calendar size={18} />
                  Información del Sistema
                </h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-600">ID:</p>
                    <p className="font-semibold">{solicitud.id}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Creado por:</p>
                    <p className="font-semibold">{solicitud.usuario_creador_nombre}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Fecha de creación:</p>
                    <p className="font-semibold">
                      {new Date(solicitud.fecha_creacion).toLocaleString('es-ES')}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Última actualización:</p>
                    <p className="font-semibold">
                      {new Date(solicitud.updated_at).toLocaleString('es-ES')}
                    </p>
                  </div>
                  {solicitud.usuario_asignado_nombre && (
                    <div>
                      <p className="text-gray-600">Asignado a:</p>
                      <p className="font-semibold">👤 {solicitud.usuario_asignado_nombre}</p>
                    </div>
                  )}
                  {solicitud.demorado === 1 && (
                    <div className="col-span-2">
                      <div className="bg-red-100 border border-red-300 rounded p-2 flex items-center gap-2">
                        <AlertCircle size={18} className="text-red-600" />
                        <p className="text-red-800 font-semibold">⚠️ Esta solicitud está demorada</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-4 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {isViewMode ? 'Cerrar' : 'Cancelar'}
          </button>
          {!isViewMode && (
            <button
              onClick={handleSubmit}
              disabled={loading || subiendoArchivo}
              className={`px-6 py-2 rounded-lg text-white ${
                loading || subiendoArchivo
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading ? 'Guardando...' : subiendoArchivo ? 'Subiendo archivos...' : 'Guardar'}
            </button>
          )}
        </div>
      </div>
      {ModalRenderer}
    </div>
  );


};



export default SolicitudesGestionView;